import { PluginManager } from './core/plugin-manager.js';
import { PLUGIN_REGISTRY, PLUGIN_CLASS_MAP } from './plugins/index.js';
import { initializeABTesting, assignTestGroup, DEFAULT_TEST_NAME, DEFAULT_APPLY_RATE } from './core/ab-testing.js';
import { initializeAnalytics, setGoogleAnalyticsUserProperties } from './core/google-analytics.js';
/**
 * Main entry point for Booster Landing Optimizer
 */
export class BoosterOptimizer {
    constructor(config = {}, scriptUrl) {
        this.testGroup = 0; // Default to control group
        this.abTestConfig = {
            testName: DEFAULT_TEST_NAME,
            applyRate: DEFAULT_APPLY_RATE
        };
        this.isStorageAvailable = false;
        this.config = config;
        this.startTime = performance.now();
        // Initialize A/B testing
        this.initializeABTesting(scriptUrl);
        // Initialize analytics
        this.initializeAnalytics();
        // Initialize plugin manager
        this.pluginManager = new PluginManager({
            performanceBudgets: config.performanceBudgets,
            pluginDefaults: {
                debug: config.debug,
                testGroup: this.testGroup // Pass test group to plugins
            }
        });
        // Log initialization
        if (config.debug) {
            console.log('[BoosterOptimizer] Initializing with config:', config);
            console.log('[BoosterOptimizer] Test group assignment:', this.testGroup);
        }
    }
    /**
     * Initialize A/B testing with configuration from config or script URL
     */
    initializeABTesting(scriptUrl) {
        try {
            const abConfig = this.config.abTesting;
            const enabled = abConfig?.enabled !== false; // Default to enabled unless explicitly disabled
            if (!enabled) {
                console.log('[BoosterOptimizer] A/B testing disabled by configuration');
                this.abTestConfig = {
                    testName: DEFAULT_TEST_NAME,
                    applyRate: DEFAULT_APPLY_RATE
                };
                return;
            }
            // Initialize A/B testing with script URL for parameter parsing
            const result = initializeABTesting(scriptUrl);
            this.testGroup = result.testGroup;
            this.isStorageAvailable = result.isStorageAvailable;
            // Override with config values if provided
            this.abTestConfig = {
                testName: abConfig?.testName || result.config.testName,
                applyRate: abConfig?.applyRate ?? result.config.applyRate,
                forceGroup: abConfig?.forceGroup ?? result.config.forceGroup
            };
            // Re-assign if config override changes parameters
            if (abConfig?.applyRate !== undefined || abConfig?.forceGroup !== undefined) {
                this.testGroup = assignTestGroup(this.abTestConfig);
            }
            console.log('[BoosterOptimizer] A/B testing initialized:', {
                testGroup: this.testGroup,
                config: this.abTestConfig,
                storageAvailable: this.isStorageAvailable
            });
        }
        catch (error) {
            console.error('[BoosterOptimizer] A/B testing initialization failed:', error);
            this.testGroup = 0; // Default to control group on error
            this.abTestConfig = {
                testName: DEFAULT_TEST_NAME,
                applyRate: DEFAULT_APPLY_RATE
            };
        }
    }
    /**
     * Initialize analytics integration
     */
    initializeAnalytics() {
        try {
            const analyticsConfig = {
                enabled: true,
                trackTestGroups: true,
                trackPerformance: true,
                ...this.config.analytics
            };
            if (analyticsConfig.enabled) {
                initializeAnalytics(analyticsConfig);
                // Set user properties with test group assignment
                setGoogleAnalyticsUserProperties();
                console.log('[BoosterOptimizer] Analytics initialized');
            }
            else {
                console.log('[BoosterOptimizer] Analytics disabled by configuration');
            }
        }
        catch (error) {
            console.error('[BoosterOptimizer] Analytics initialization failed:', error);
        }
    }
    /**
     * Initialize and execute the optimizer
     */
    async run() {
        try {
            // Record initialization start
            if (window.__OPTIMIZER_METRICS__) {
                window.__OPTIMIZER_METRICS__.initTime = performance.now() - this.startTime;
            }
            // Only proceed with optimization for treatment groups (groups 1 and 3)
            const shouldOptimize = this.testGroup === 1 || this.testGroup === 3;
            if (!shouldOptimize) {
                if (this.config.debug) {
                    console.log(`[BoosterOptimizer] Control group (${this.testGroup}) - skipping optimization`);
                }
                return;
            }
            if (this.config.debug) {
                console.log(`[BoosterOptimizer] Treatment group (${this.testGroup}) - running optimization`);
            }
            // Create and register plugins
            this.createAndRegisterPlugins();
            // Initialize plugins
            const initResults = await this.pluginManager.initialize();
            if (this.config.debug) {
                console.log('[BoosterOptimizer] Plugin initialization results:', initResults);
            }
            // Execute plugins after DOM is ready
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.execute());
            }
            else {
                await this.execute();
            }
        }
        catch (error) {
            console.error('[BoosterOptimizer] Failed to run:', error);
            throw error;
        }
    }
    /**
     * Create plugin instances and register them in execution order
     */
    createAndRegisterPlugins() {
        const plugins = [];
        // Get requested plugin names or default to all plugins
        const requestedPlugins = this.config.plugins || Object.keys(PLUGIN_CLASS_MAP);
        // Create plugins in the defined execution order
        for (const PluginClass of PLUGIN_REGISTRY) {
            const pluginName = PluginClass.name;
            // Only create plugin if it was requested
            if (requestedPlugins.includes(pluginName)) {
                try {
                    const config = this.config.pluginConfigs?.[pluginName] || {};
                    const plugin = new PluginClass(config);
                    plugins.push(plugin);
                    if (this.config.debug) {
                        console.log(`[BoosterOptimizer] Created plugin: ${pluginName}`);
                    }
                }
                catch (error) {
                    console.error(`[BoosterOptimizer] Failed to create plugin ${pluginName}:`, error);
                }
            }
        }
        // Register all plugins with the manager
        this.pluginManager.registerPlugins(plugins);
    }
    /**
     * Execute all plugins
     */
    async execute() {
        try {
            const results = await this.pluginManager.execute();
            if (this.config.debug) {
                console.log('[BoosterOptimizer] Plugin execution results:', results);
                this.logPerformanceMetrics();
            }
        }
        catch (error) {
            console.error('[BoosterOptimizer] Execution failed:', error);
            throw error;
        }
    }
    /**
     * Get all performance metrics
     */
    getMetrics() {
        return this.pluginManager.getAllMetrics();
    }
    /**
     * Get current test group assignment
     */
    getTestGroup() {
        return this.testGroup;
    }
    /**
     * Get A/B test configuration
     */
    getABTestConfig() {
        return this.abTestConfig;
    }
    /**
     * Check if user is in treatment group (should receive optimizations)
     */
    isTreatmentGroup() {
        return this.testGroup === 1 || this.testGroup === 3;
    }
    /**
     * Check if user is in control group (should not receive optimizations)
     */
    isControlGroup() {
        return this.testGroup === 0 || this.testGroup === 2;
    }
    /**
     * Log performance metrics to console
     */
    logPerformanceMetrics() {
        const metrics = this.getMetrics();
        console.group('[BoosterOptimizer] Performance Metrics');
        console.log('Global metrics:', metrics.global);
        console.log('Plugin metrics:', metrics.plugins);
        console.log('Performance summary:', metrics.performance.summary);
        if (metrics.performance.violations.length > 0) {
            console.warn('Performance violations:', metrics.performance.violations);
        }
        console.groupEnd();
    }
    /**
     * Clean up resources
     */
    async cleanup() {
        try {
            await this.pluginManager.cleanup();
            console.log('[BoosterOptimizer] Cleanup completed');
        }
        catch (error) {
            console.error('[BoosterOptimizer] Cleanup failed:', error);
        }
    }
}
/**
 * Auto-initialize if script tag has data-auto-init attribute
 */
if (typeof window !== 'undefined' && document.currentScript) {
    const script = document.currentScript;
    const autoInit = script.dataset.autoInit === 'true';
    if (autoInit) {
        // Parse config from data attributes
        const config = {};
        if (script.dataset.debug === 'true') {
            config.debug = true;
        }
        if (script.dataset.plugins) {
            config.plugins = script.dataset.plugins.split(',').map(p => p.trim());
        }
        // Parse A/B testing config from data attributes
        if (script.dataset.abTesting === 'false') {
            config.abTesting = { enabled: false };
        }
        else {
            config.abTesting = {};
            if (script.dataset.testName) {
                config.abTesting.testName = script.dataset.testName;
            }
            if (script.dataset.applyRate) {
                const rate = parseFloat(script.dataset.applyRate);
                if (!isNaN(rate) && rate >= 0 && rate <= 100) {
                    config.abTesting.applyRate = rate / 100;
                }
            }
            if (['0', '1', '2', '3'].includes(script.dataset.forceGroup || '')) {
                config.abTesting.forceGroup = parseInt(script.dataset.forceGroup);
            }
        }
        // Parse analytics config from data attributes
        if (script.dataset.analytics === 'false') {
            config.analytics = { enabled: false, trackTestGroups: false, trackPerformance: false };
        }
        // Initialize and run with script URL for parameter parsing
        const optimizer = new BoosterOptimizer(config, script.src);
        // Make optimizer available globally for debugging
        window.__BOOSTER_OPTIMIZER__ = optimizer;
        // Run optimizer
        optimizer.run().catch(error => {
            console.error('[BoosterOptimizer] Auto-initialization failed:', error);
        });
    }
}
// Export for manual initialization
export { PluginManager } from './core/plugin-manager.js';
export { Plugin, OptimizedPlugin } from './core/plugin-interface.js';
export { PerformanceMonitor } from './core/performance-monitor.js';
// Export A/B testing and analytics modules
export { initializeABTesting, assignTestGroup, getTestGroup, parseApplyRateFromUrl, parseForcedGroupFromUrl, DEFAULT_TEST_NAME, DEFAULT_APPLY_RATE } from './core/ab-testing.js';
export { initializeAnalytics, setGoogleAnalyticsUserProperties } from './core/google-analytics.js';
//# sourceMappingURL=index.js.map